import { URLS } from '@/constants';
import { PaginatedResponse } from '@/models/api';
import { PaymentOption } from '@/models/company';
import { Contact } from '@/models/contact';
import { Credit, CreditDownload } from '@/models/credit';
import { Customer, CustomerGroup, ServiceZone, Zone } from '@/models/customer';
import { CustomerContact } from '@/models/customerContact';
import { CustomerStats } from '@/models/customerStats';
import { CustomerStatus } from '@/models/customerStatus';
import { UpdateCustomMenuCustomer } from '@/models/customMenus';
import { LicenseType } from '@/models/licenseType';
import { Product } from '@/models/product';
import { Task } from '@/models/task';
import { useUserStore } from '@/piniaStores/user/user.store';
import { CreditResponse } from '@/services/api/models/credit';
import {
  BaseGetCustomerRequestPayload,
  GetCustomerRecordByIdRequestPayload,
  GetPotentialCustomerListRequestPayload,
  PotentialCustomer,
} from '@/services/api/models/customer';
import { CustomerContactResponse } from '@/services/api/models/customerContact';
import { CustomerStatsResponse } from '@/services/api/models/customerStats';
import { CustomerParsed, parseCustomers } from '@/services/parsers/CRM';
import session, { apiSlugSession, createSession } from '@/services/session';
import { buildPath } from '@/utils';
import { formatDateTimeUTC, subtractTime } from '@/utils/dates';
import JSONTransform from '@/utils/json-transform';

// #region Modern CRM Customer Requests

/**
 * Get a list of customers
 */
export const getCustomerList = async (payload: BaseGetCustomerRequestPayload): Promise<CustomerParsed[]> => {
  const { isBrandOwner, ...otherParams } = payload;

  const path = isBrandOwner ? `${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.CUSTOMERS}` : URLS.CUSTOMER.LIST;

  const params = {
    archived: false,
    include_children: ['license_type', 'taxes', 'shipping_charge'].join(','),
    search: payload.search,
    ...otherParams,
  };

  // https://github.com/LeafLink/marketplace-web/commit/d947d7a4fb5e11b05351e6d1586c06967680d052
  // in the original getCustomers we deleted the archived param when it's set to true
  // when archived is not set to true we want it to default to false.
  if (payload.archived) {
    // @ts-ignore: allow archive:false to be set as a default and deleted here
    delete params.archived;
  } else if (payload.archived === undefined) {
    // ensure archived is set to false when the spread of payload overrides archived to be undefined
    params.archived = false;
  }

  const { data: customers } = await apiSlugSession.get<Customer[]>(`${path}/`, {
    params: { ...params, error_format: 'jsonapi' },
  });

  return customers.map((customer) => ({
    ...customer,
    isPaymentsEnabledForPartner: customer.partnerEnablePaymentsDirect || customer.partnerEnablePaymentsFlexible,
  }));
};

/**
 * Get a detailed customer record by customer ID
 */
export const getCustomerRecordById = async (payload: GetCustomerRecordByIdRequestPayload): Promise<Customer> => {
  const { customerId, isBrandOwner, ...otherParams } = payload;

  const path = isBrandOwner ? `${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.CUSTOMERS}` : URLS.CUSTOMER.LIST;

  const params: {
    archived?: boolean;
    include_children: string;
    search?: string;
  } = {
    archived: false,
    include_children: ['license_type', 'taxes', 'shipping_charge', 'contacts'].join(','),
    search: payload.search,
    ...otherParams,
  };

  // in the original getCustomers we deleted the archived param when it's set to true.
  if (payload.archived) {
    delete params.archived;
  } else if (payload.archived === undefined) {
    // ensure archived is set to false when the spread of payload overrides archived to be undefined
    params.archived = false;
  }

  const { data: customer } = await apiSlugSession.get<Customer>(`${path}/${customerId}/`, {
    params: { ...params, error_format: 'jsonapi' },
  });

  return customer;
};

/**
 * Get a list of potential customers
 */
export const getPotentialCustomerList = async ({ queryString }: GetPotentialCustomerListRequestPayload) => {
  // converting the query string into an object to pass as params
  const queryParams = new URLSearchParams(queryString);

  const params = { ...Object.fromEntries(queryParams), error_format: 'jsonapi' };

  const { data: potentialCustomers } = await session.get<PaginatedResponse<PotentialCustomer>>(
    URLS.CUSTOMER.POTENTIAL_CUSTOMERS,
    { params },
  );

  return potentialCustomers;
};
// #endregion

/**
 * Get all Customers of the current Company
 * @param {object} [payload] optional to pass params
 * @param {number} [payload.customerId] optional id to fetch a detailed Customer record
 * @param {boolean} [payload.isBrandOwner] optional to fetch BrandOwner Customers
 * @return {Promise<Customer[]>} A Promise to an array of Customer(s)
 * @deprecated Use `getCustomerList` or `getCustomerRecordById` instead
 */
export const getCustomers = (payload) => {
  const { customerId, isBrandOwner, ...queryParams } = payload;
  const path = isBrandOwner ? `${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.CUSTOMERS}` : URLS.CUSTOMER.LIST;
  const customerPath = customerId ? `/${customerId}` : '';

  const params = {
    archived: false,
    include_children: ['license_type', 'taxes', 'shipping_charge'].join(','),
    search: payload.search,
    ...queryParams,
  };

  // if the request needs archived customers as well we remove the filter
  if (payload.archived) {
    delete params.archived;
  }

  return apiSlugSession
    .get<Customer[] | Customer>(`${path}${customerPath}/?${URLS.CUSTOM_ERROR}`, { params })
    .then((response) => parseCustomers(response.data));
};

/**
 * Get potential customers
 * @deprecated use `getPotentialCustomerList` instead
 */
export const getPotentialCustomers = (params = 'limit=5000') => {
  return session
    .get<PaginatedResponse<PotentialCustomer>>(`${URLS.CUSTOMER.POTENTIAL_CUSTOMERS}/?${params}&${URLS.CUSTOM_ERROR}`)
    .then((response) => response.data);
};

/**
 * Adds a new Customer
 * @param {Customer} customer The data representing the new Customer.
 */
export const addCustomer = (customer: Customer) => {
  return apiSlugSession.post(`${URLS.CUSTOMER.LIST}/?${URLS.CUSTOM_ERROR}`, customer).then((response) => response.data);
};

/**
 * Removes a Customer
 * @param {String} id The customer id string.
 */
export const archiveCustomer = (id: number | string) => {
  return apiSlugSession.delete(`${URLS.CUSTOMER.LIST}/${id}/?${URLS.CUSTOM_ERROR}`).then((response) => response.data);
};

/**
 * Updates an existing Customer
 * @param {Customer} customer The object containing updated Customer data.
 */
export const updateCustomer = (customer: Customer) => {
  return apiSlugSession
    .patch(`${URLS.CUSTOMER.LIST}/${customer.id}/?${URLS.CUSTOM_ERROR}`, customer)
    .then((response) => response.data);
};

/**
 * Patch update the customers in bulk.
 * This is used in Custom Menus
 * @param customers - customers to be updated
 */
export const updateCustomersBulk = (customers: (UpdateCustomMenuCustomer | Customer)[]) => {
  return apiSlugSession
    .patch<Customer[]>(`${URLS.CUSTOMER.BULK}/?bulk=true`, customers)
    .then((response) => response.data);
};

/**
 * Get all Contacts of a particular Company
 */
export const getContacts = () => {
  return apiSlugSession
    .get<Contact[]>(`${URLS.CUSTOMER.CONTACTS}/?${URLS.CUSTOM_ERROR}`)
    .then((response) => response.data);
};

/**
 * Get all CustomerContacts join objects for the current Company
 * Ignores CustomerContacts for archived customers or contacts by default
 * @param {boolean} contactArchived Pull join objects for archived contacts, default false
 * @param {boolean} customerArchived Pull join objects for archived customers, default false
 */
export const getCustomerContacts = ({ contactArchived = false, customerArchived = false } = {}): Promise<
  CustomerContact[]
> => {
  return apiSlugSession
    .get<CustomerContactResponse[]>(
      `${URLS.CUSTOMER.CUSTOMER_CONTACTS}/?contact_archived=${contactArchived}&customer_archived=${customerArchived}&${URLS.CUSTOM_ERROR}`,
    )
    .then((response) =>
      // TODO: Remove this stupid mutation of the response
      response.data.map((customerContact) => {
        return {
          id: customerContact.id,
          contactId: customerContact.contactId,
          customerId: customerContact.companyId,
          isPrimary: customerContact.isPrimary,
        };
      }),
    );
};

/**
 * Update a CustomerContact record. Should be used only for setting as primary.
 * @param {CustomerContact} the PATCH data for the CompanyCustomer
 */
export const updateCustomerContact = (customerContact: CustomerContact) => {
  return session
    .patch(`${URLS.CUSTOMER.CUSTOMER_CONTACT_DETAIL}/${customerContact.id}/?${URLS.CUSTOM_ERROR}`, customerContact)
    .then((response) => response.data);
};

/**
 * Adds a new Contact
 * @param {Contact} contact The updated Contact object.
 */
export const addContact = (contact: Contact) => {
  return apiSlugSession.post(`${URLS.CUSTOMER.CONTACTS}/?${URLS.CUSTOM_ERROR}`, contact).then((response) => ({
    ...response.data,
    customerIds: (response.data.companyCustomers || []).map((c) => c.companyCustomerId),
  })); // TODO: Add in primary ids when needed / Need to match get list which has an optimized version of these keys.
};

/**
 * Updates a Contact
 * @param {Contact} contact The data representing the Contact.
 */
export const updateContact = (contact: Contact) => {
  return apiSlugSession
    .patch(`${URLS.CUSTOMER.CONTACTS}/${contact.id}/?${URLS.CUSTOM_ERROR}`, contact)
    .then((response) => ({
      ...response.data,
      customerIds: (response.data.companyCustomers || []).map((c) => c.companyCustomerId),
    })); // TODO: Add in primary ids when needed / Need to match get list which has an optimized version of these keys.
};

/**
 * Delete a Contact
 * @param {Contact} contact The data representing the Contact.
 */
export const deleteContact = (contact: Contact) => {
  return apiSlugSession
    .delete(`${URLS.CUSTOMER.CONTACTS}/${contact.id}/?${URLS.CUSTOM_ERROR}`)
    .then((response) => response.data);
};

/**
 * Get a list of all Customer Statuses
 * @param params Optional query params
 */
export const getStatuses = (params?: object) => {
  return apiSlugSession
    .get<CustomerStatus[]>(`${URLS.CUSTOMER.STATUSES}/?${URLS.CUSTOM_ERROR}`, { params })
    .then((response) =>
      response.data.map((item) => {
        const { id, state, hexColor } = item;

        return {
          id,
          name: state,
          hexCode: hexColor,
        };
      }),
    );
};

/**
 * Fetches a list of company tags that are applied to customer in the CRM.
 * @return A Promise to an array of tag objects
 */
export const getTags = () => {
  return apiSlugSession.get(`${URLS.CUSTOMER.TAGS}/?${URLS.CUSTOM_ERROR}`).then((response) => response.data);
};

export interface GetZonesRequestPayload {
  fields?: Array<keyof Zone>; // array of properties
  zipcodes?: number[];
}

/**
 * Gets all Distribution Zone(s) of a distributor
 * @param payload
 * @param payload.fields - which fields to return for a zone
 * @param payload.zipcodes - which zipcodes to search for zones by
 */
export const getDistributionZones = async (payload?: GetZonesRequestPayload) => {
  const params: {
    fields?: string;
    zipcodes?: string;
  } = {
    fields: payload?.fields?.join(','),
    zipcodes: payload?.zipcodes?.join(','),
  };

  const url = `${URLS.CUSTOMER.DELIVERY_ZONES}/?${URLS.CUSTOM_ERROR}`;
  const response = await apiSlugSession.get<Zone[]>(url, { params });

  return response.data;
};

/**
 * Get a Distribution Zone of a distributor
 * @param zoneId - distribution zone id
 */
export const getDistributionZone = async (zoneId: string) => {
  const path = buildPath(URLS.CUSTOMER.DELIVERY_ZONE, { zoneId });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.get<Zone>(url);

  // TODO: Consider updating the return type away from an array
  // The corresponding action `fetchDistributionZone`, uses the same mutation as
  // `fetchDistributionZones`, setting `state.zones` which is an array.
  // `fetchDistributionZone` is not currently used.
  return [response.data];
};

export interface UpdateZoneRequestPayload extends Partial<Zone> {
  id: Zone['id'];
}

/**
 * Patches a Distribution Zone
 * @param zone The data to update the Zone with
 */
export const updateZone = async (zone: UpdateZoneRequestPayload) => {
  const path = buildPath(URLS.CUSTOMER.DELIVERY_ZONE, { zoneId: zone.id });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.patch(url, zone);

  return response.data;
};

/**
 * Creates a new Zone.
 * @param zone The data to create a new Zone from.
 */
export const addZone = async (zone: Omit<Zone, 'id'>) => {
  const response = await apiSlugSession.post(`${URLS.CUSTOMER.DELIVERY_ZONES}/?${URLS.CUSTOM_ERROR}`, zone);

  return response.data;
};

export interface BulkSaveZonesRequestPayload {
  name?: Zone['name'];
  zipCodes?: Zone['zipCodes'];
}

/**
 * Bulk imports new Zones.
 * @param zones The data to create or update Zones from.
 */
export const bulkSaveZones = (zones: BulkSaveZonesRequestPayload[]) => {
  return apiSlugSession.post(`${URLS.CUSTOMER.DELIVERY_ZONES}/?bulk=true`, zones).then((response) => response.data);
};

/**
 * Removes the provided zone.
 */
export const removeZone = async (zone: Zone) => {
  const path = buildPath(URLS.CUSTOMER.DELIVERY_ZONE, { zoneId: zone.id });
  const params = new JSONTransform({ errorFormat: 'jsonapi' }).toSnakeCaseKeys();

  const response = await apiSlugSession.delete(path, { params });

  return response.data;
};

/**
 * Get all available zip codes available to a Distribution Zone
 */
export const getZips = async () => {
  const response = await apiSlugSession.get(`${URLS.CUSTOMER.DELIVERY_ZONE_ZIP_CODES}?${URLS.CUSTOM_ERROR}`);

  return response.data;
};

/**
 * Creates a new service zone.
 * @param {ServiceZone} serviceZone to create
 */
export const addServiceZone = (serviceZone: Omit<ServiceZone, 'id'>) => {
  return session
    .post(`${URLS.CUSTOMER.SERVICE_ZONES}/?${URLS.CUSTOM_ERROR}`, serviceZone)
    .then((response) => response.data);
};

/**
 * Updates a service zone
 * @param {ServiceZone} serviceZone to update
 */
export const updateServiceZone = (serviceZone: ServiceZone) => {
  return session
    .patch(`${URLS.CUSTOMER.SERVICE_ZONES}/${serviceZone.id}/?${URLS.CUSTOM_ERROR}`, serviceZone)
    .then((response) => response.data);
};

/**
 * Removes a service zone
 * @param {ServiceZone} serviceZone to remove
 */
export const removeServiceZone = (serviceZone: ServiceZone) => {
  return session
    .delete(`${URLS.CUSTOMER.SERVICE_ZONES}/${serviceZone.id}/?${URLS.CUSTOM_ERROR}`)
    .then((response) => response.data);
};

/**
 * Gets all Service Zones related to the current signed user
 */
export const getServiceZones = () => {
  return apiSlugSession
    .get<ServiceZone[]>(`${URLS.CUSTOMER.SERVICE_ZONES}/?${URLS.CUSTOM_ERROR}`)
    .then((response) => response.data);
};

//////////////////////////////////////////
// VUETIFY ///////////////////////////////
//////////////////////////////////////////

/**
 * Get all available License Types for Company
 */
export const getLicenseTypes = () => {
  return apiSlugSession
    .get<LicenseType[]>(`${URLS.CUSTOMER.LICENSE_TYPES}/?${URLS.CUSTOM_ERROR}`)
    .then((response) => response.data);
};

/**
 * Get all available Payment Terms by Company
 */
export const getPaymentTerms = () => {
  return apiSlugSession.get(`${URLS.CUSTOMER.PAYMENT_TERMS}/?${URLS.CUSTOM_ERROR}`).then((response) => response.data);
};

/**
 * Get Customer Tiers by Company
 */
export const getTiers = () => {
  return session.get(`${URLS.CUSTOMER.TIERS}/?${URLS.CUSTOM_ERROR}`).then((response) => response.data);
};

/**
 * Saves an array of customer rows
 * @param {Object} data
 * @param {Boolean} validate
 * @param {function} onUploadProgress
 */
export const saveCSVRows = ({ data, validate = true, onUploadProgress }) => {
  const userStore = useUserStore();

  const url = `${userStore.userActiveCompany?.slug}/${URLS.CUSTOMER.IMPORT}/`;
  const params = { error_format: 'jsonapi', validate: !!validate };

  return createSession(URLS.INTERNAL, { shouldTransform: true, onUploadProgress })
    .post(url, data, { params })
    .then((response) => response.data);
};

/**
 * Get Array of Customer Stats
 */
export const getStats = () => {
  return apiSlugSession
    .get<CustomerStatsResponse[]>(`${URLS.CUSTOMER.STATS}/?${URLS.CUSTOM_ERROR}`)
    .then((response) => {
      const customerStats: CustomerStats = {};

      response.data.forEach((item) => {
        customerStats[item.id] = {
          ...item,
          firstOrderDate: item.firstOrderDate?.substring(0, 10) ?? null,
          lastOrderDate: item.lastOrderDate?.substring(0, 10) ?? null,
        };
      });

      return customerStats;
    });
};

/**
 * Get Array of tasks by company
 * @param {Object} payload
 * @param {Number} payload.days number of days to subtract from Now
 */
export const getTasks = (payload?: { days: number }) => {
  const params = {
    due_date__gte: '',
    delete: false,
  };

  if (payload?.days) {
    params.due_date__gte = formatDateTimeUTC(subtractTime(null, { days: payload.days }));
  }

  return apiSlugSession.get<Task[]>(`${URLS.CUSTOMER.TASKS}/?${URLS.CUSTOM_ERROR}`, { params }).then((response) =>
    response.data.map((t) => ({
      ...t,
      dateCreated: formatDateTimeUTC(t.dateCreated),
      dueDate: formatDateTimeUTC(t.dueDate),
    })),
  );
};

/**
 * Get a single task by company
 * @param {Number} taskId
 */
export const getTask = (taskId: number) => {
  return apiSlugSession.get(`${URLS.CUSTOMER.TASKS}/${taskId}/?${URLS.CUSTOM_ERROR}`).then((response) => {
    response.data.dueDate = formatDateTimeUTC(response.data.dueDate);
    return response.data;
  });
};

/**
 * Create new task
 * @param {Object} task
 */
export const saveTask = (task) => {
  return apiSlugSession.post(`${URLS.CUSTOMER.TASKS}/?${URLS.CUSTOM_ERROR}`, task).then((response) => {
    if (Array.isArray(response.data)) {
      return response.data.map((t) => ({
        ...t,
        dateCreated: formatDateTimeUTC(t.dateCreated),
        dueDate: formatDateTimeUTC(t.dueDate),
      }));
    }

    response.data.dueDate = formatDateTimeUTC(response.data.dueDate);
    return response.data;
  });
};

/**
 * Activate customer on payments
 * @param {Object}
 */
export const activatePaymentOptions = (params) => {
  return session.post(`${URLS.CUSTOMER.CUSTOMER_PAYMENT_OPTIONS}/`, params).then((response) => response.data);
};

/**
 * Update task
 * @param {Object} task
 */
export const updateTask = (task) => {
  return apiSlugSession.patch(`${URLS.CUSTOMER.TASKS}/${task.id}/?${URLS.CUSTOM_ERROR}`, task).then((response) => {
    response.data.dueDate = formatDateTimeUTC(response.data.dueDate);
    return response.data;
  });
};

/**
 * Fetches a list of credits belongs to the company
 */
export const getCredits = (params) => {
  return session
    .get<CreditResponse[]>(`${URLS.COMPANY.CREDITS}/?${URLS.CUSTOM_ERROR}`, { params })
    .then((response) => response.data);
};

/**
 * Adds a new credit
 * @param {Credit} credit to create
 */
export const addCredit = (credit: Credit) => {
  return session.post(`${URLS.COMPANY.CREDITS}/?${URLS.CUSTOM_ERROR}`, credit).then((response) => response.data);
};

/**
 * Updates a credit
 * @param {Credit} credit to update
 */
export const updateCredit = (credit: Credit) => {
  return session
    .patch(`${URLS.COMPANY.CREDITS}/${credit.id}/?${URLS.CUSTOM_ERROR}`, credit)
    .then((response) => response.data);
};

/**
 * Removes a credit from an order by setting its appliedTo field empty
 * @param {number} creditId to be remove
 */
export const removeCreditFromOrder = (creditId: number) => {
  return session
    .patch(`${URLS.COMPANY.CREDITS}/${creditId}/?${URLS.CUSTOM_ERROR}`, { appliedTo: '' })
    .then((response) => response.data);
};

/**
 * Export credits
 */
export const exportCredits = (creditDownload: CreditDownload) => {
  return session
    .post(`${URLS.COMPANY.CREDIT_DOWNLOADS}/?${URLS.CUSTOM_ERROR}`, creditDownload)
    .then((response) => response.data);
};

/**
 * Fetches sellers Inbound Leads
 */
export const getInboundLeads = () => {
  const params = {
    limit: 5,
  };

  return apiSlugSession
    .get(`${URLS.CUSTOMER.CUSTOMER_REQUESTS}/?${URLS.CUSTOM_ERROR}`, { params })
    .then((response) => response.data);
};

/**
 * Get a list of customerIds that can buy a product
 * @param {Array[number]} productIds collection of productId's
 * @returns {number[]}
 */
export const getEligibleBuyersForProducts = async (productIds: Product['id'][]) => {
  const param = productIds.length ? productIds.join(',') : '';
  const response = await apiSlugSession.get(`${URLS.CUSTOMER.ELIGIBLE_CUSTOMERS}/?product_ids=${param}`);

  return response.data;
};

/**
 * Get a list of customer groups
 * @returns {CustomerGroup[]}
 */
export const getCustomerGroups = async () => {
  const response = await apiSlugSession.get(`${URLS.CUSTOMER.GROUPS}/?${URLS.CUSTOM_ERROR}`);

  return <CustomerGroup[]>response.data;
};

/**
 * Create a customer group
 * @param {CustomerGroup} group
 */
export const createCustomerGroup = async (group: CustomerGroup) => {
  const response = await apiSlugSession.post(`${URLS.CUSTOMER.GROUPS}/?${URLS.CUSTOM_ERROR}`, group);

  return response.data;
};

/**
 * Update a customer group
 * @param {CustomerGroup} group
 */
export const updateCustomerGroup = async (group: CustomerGroup) => {
  const response = await apiSlugSession.put(`${URLS.CUSTOMER.GROUPS}/${group.id}/?${URLS.CUSTOM_ERROR}`, group);

  return response.data;
};

/**
 * Remove a customer group
 * @param {number} customerGroupId
 * @returns
 */
export const removeCustomerGroup = async (customerGroupId: number) => {
  const response = await apiSlugSession.delete(`${URLS.CUSTOMER.GROUPS}/${customerGroupId}?${URLS.CUSTOM_ERROR}`);

  return response.data;
};

/**
 * Get payment options for company customer
 * @return Promise
 */
export const getCustomerPaymentOptions = async ({ customerId }): Promise<PaymentOption[]> => {
  const { LIST, PAYMENT_OPTIONS } = URLS.CUSTOMER;
  const response = await apiSlugSession.get(`${LIST}/${customerId}/${PAYMENT_OPTIONS}/?${URLS.CUSTOM_ERROR}`);

  return response.data;
};

/**
 * Get payment options for company customer order (buyer-side)
 * @return Promise
 */
export const getCustomerOrderPaymentOptions = async ({ customerId }) => {
  const response = await apiSlugSession.get<PaymentOption[]>(
    `${URLS.CUSTOMER.ORDER_PAYMENT_OPTIONS}/${customerId}/?${URLS.CUSTOM_ERROR}`,
  );

  return response.data;
};

interface InviteCustomerPayload {
  customerId: string;
}

/**
 * Sends a company a Payments invitation
 * @param {Customer} customer The data representing the new Customer.
 */
export const inviteCustomerToPayments = ({ customerId }: InviteCustomerPayload) => {
  const path = buildPath(URLS.CUSTOMER.PAYMENTS_INVITE, { customerId });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;

  return apiSlugSession.post(url, { customerId }).then((response) => response.data);
};
